<template>

  <!-- Back button for order details -->
  <div v-if="selectedOrderNo">
    <button @click="selectedOrderNo = null" class="go-back-button">{{ $translate('BACK_TO_ORDER_HISTORY') }}</button>
  </div>
  <h1>{{ selectedOrderNo ? $translate('C_ORDER_DETAILS_HEADING') + selectedOrderNo : $translate('HEADING_TITLE') }}</h1>

  <!-- Orders table -->
  <div class="table-wrapper" v-if="!selectedOrderNo">
    <table class="unstriped">
      <thead>
        <tr>
          <th scope="col" class="text-left">{{ $translate('ACCOUNT_ORDER_ORDERID') }}</th>
          <th scope="col" class="text-left">{{ $translate('ACCOUNT_ORDER_DATE') }}</th>
          <th scope="col" class="text-left">{{ $translate('ACCOUNT_ORDER_STATUS') }}</th>
          <th scope="col" class="text-left hide-for-small">{{ $translate('ACCOUNT_ORDER_ORDERTOTAL') }}</th>
          <th scope="col" class="text-right">{{ $translate('HEADER_PRODUCTS_SHOW') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="order in orders" :key="order.OrderNo">
          <td>{{ order.OrderNo }}</td>
          <td>{{ order.OrderDateFormatted }}</td>
          <td>{{ order.Status }}</td>
          <td class=" hide-for-small">{{ formatOrderValue(order.OrderValue) }}</td>
          <td class="text-right">
            <a @click.prevent="viewOrderDetails(order)" class="dark underline">{{ $translate("HEADER_PRODUCTS_SHOW") }}</a>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  
  <!-- Pagination -->
  <div class="pagination-wrapper" v-if="!selectedOrderNo">
    <div v-if="!selectedOrderNo && totalCount > 0" class="pagination-wrapper__order-count">
      {{ orderCountText }}
    </div>
    <div class="pagination" v-if="!selectedOrderNo">
      <button class="previous" @click="currentPage--" :disabled="currentPage <= 1">{{ $translate('PREVNEXT_TITLE_PREV') }}</button>
      <button
        v-for="n in visiblePages"
        :key="n.key"
        @click="typeof n.page === 'number' && (currentPage = n.page)"
        :disabled="typeof n.page !== 'number'"
        :class="{ current: currentPage === n.page, dots: n.page === 'dots' }"
      >
        {{ n.page === 'dots' ? '...' : n.page }}
      </button>
      <button class="next" @click="currentPage++" :disabled="currentPage >= totalPages">{{ $translate('PREVNEXT_TITLE_NEXT') }}</button>
    </div>
  </div>

  <!-- Order details -->
  <div v-if="selectedOrderNo">
    <OrderDetails :order-no="selectedOrderNo" :order-data="selectedOrderData" :currency="props.currency"/>
  </div>
</template>

<script setup>
import { ref, computed, watchEffect, inject } from 'vue';
import OrderDetails from '@/components/pullapi/OrderDetails.vue';
import { useCurrencyFormatter } from '@/composables/use-currency-formatter.js';

const { $translate } = inject('i18n');
const API_URL = '/ajax.php?mode=external_fetchdata';
let orders = ref([]);
const selectedOrderNo = ref(null)
const pageSize = 10
const currentPage = ref(1)
const totalCount = ref(0);
const selectedOrderData = ref(null);

const props = defineProps({
  customerNo: {
    required: true,
    type: String
  },
  currency: {
    type: String,
    required: false,
    default: 'SEK'
  }
});

// total pages
const totalPages = computed(() => Math.ceil(totalCount.value / pageSize));

// Fetch orders when page changes
watchEffect(() => {
  getOrders();
});

async function getOrders() {
  return new Promise((resolve, reject) => {
    $.ajax({
      type: "POST",
      url: API_URL,
      data: {
        key: 'CustomerOrders',
        custom_fields: {
          customer_no: props.customerNo,
          page: currentPage.value,
          page_size: pageSize
        }
      },
      success: function(data) {
        // Try to parse response to json
        try {
          const parsed = typeof data === 'string' ? JSON.parse(data) : data;
          orders.value = parsed.orders || [];
          totalCount.value = parsed.totalCount || 0;
          resolve(parsed.orders);
        } catch (e) {
          console.error("JSON parse error:", e);
          resolve([]);
        }
      },
      error: function(err) {
        console.error("Failed to fetch orders:", err);
        reject([]);
      }
    });
  });
}
function viewOrderDetails(order) {
  selectedOrderNo.value = order.OrderNo;
  selectedOrderData.value = order;
}

// Pagination
const visiblePages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 5) {
    for (let i = 1; i <= total; i++) {
      pages.push({ page: i, key: i });
    }
  } else {
    pages.push({ page: 1, key: 1 });

    if (current > 3) {
      pages.push({ page: 'dots', key: 'start-dots' });
    }

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    for (let i = start; i <= end; i++) {
      pages.push({ page: i, key: i });
    }

    if (current < total - 2) {
      pages.push({ page: 'dots', key: 'end-dots' });
    }

    pages.push({ page: total, key: total });
  }

  return pages;
});

const showingRange = computed(() => {
  const start = (currentPage.value - 1) * pageSize + 1;
  const end = Math.min(currentPage.value * pageSize, totalCount.value);
  return { start, end };
});

const orderCountText = computed(() => {
  return `${$translate('C_SHOWING')} ${showingRange.value.start} ${$translate('C_TO')} ${showingRange.value.end} (${$translate('TEXT_OF')} ${totalCount.value} ${$translate('C_ORDERS')})`;
});

const { formatPrice } = useCurrencyFormatter(props.currency);

function formatOrderValue(value) {
  if (typeof value !== 'number') return value;
  return formatPrice(value);
}

</script>